import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Loader2, Plus, Minus, Circle, GitCompare, AlertOctagon, AlertTriangle, ShieldAlert, Info, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/util";
import { toast } from "sonner";

const SEV_TONE = {
  critical: { icon: AlertOctagon, tone: "text-sev-critical", border: "border-sev-critical/50", bg: "bg-sev-critical/10", label: "CRÍTICO" },
  high:     { icon: AlertTriangle, tone: "text-sev-critical", border: "border-sev-critical/40", bg: "bg-sev-critical/5",  label: "ALTO" },
  medium:   { icon: ShieldAlert,   tone: "text-sev-medium",   border: "border-sev-medium/40",   bg: "bg-sev-medium/10",   label: "MEDIO" },
  low:      { icon: Info,          tone: "text-sev-low",      border: "border-sev-low/40",      bg: "bg-sev-low/10",      label: "BAJO" },
};

/**
 * Modal de comparativa entre corridas del mismo (tool, target).
 * Muestra: added (nuevos), resolved (resueltos), stable (persistentes).
 */
export default function ScanComparisonModal({ jobId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("added");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/scans/jobs/${jobId}/compare`);
        if (!alive) return;
        setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo comparar");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [jobId]);

  const rows = data?.[tab] || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         onClick={onClose} data-testid="scan-compare-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-4xl bg-card border border-border max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <div className="overline mb-1 flex items-center gap-2 text-primary">
              <GitCompare className="w-3 h-3" /> Comparativa entre corridas
            </div>
            <h2 className="font-display text-2xl font-medium">
              {loading ? "Cargando…" : (data?.has_previous ? "Cambios respecto a la corrida anterior" : "Sin corrida previa")}
            </h2>
            {data?.has_previous && (
              <div className="font-mono text-[10px] text-muted-foreground mt-1">
                Anterior: {data.previous_started_at ? new Date(data.previous_started_at).toLocaleString() : "—"}
                {" · Actual: this scan"}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted" data-testid="close-compare-modal">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> Cargando comparativa…
            </div>
          ) : !data?.has_previous ? (
            <div className="border border-border p-6 text-center" data-testid="compare-no-previous">
              <GitCompare className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
              <div className="font-display text-lg">Sin corrida anterior</div>
              <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                Esta es la primera corrida completada de esta herramienta sobre este objetivo.
                Después de la próxima ejecución podrás comparar los cambios aquí.
              </div>
            </div>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-3 gap-3 mb-5" data-testid="compare-kpis">
                <Kpi label="Nuevos" value={data.counters.added} icon={Plus}
                     tone="text-sev-critical" border="border-sev-critical/40" />
                <Kpi label="Resueltos" value={data.counters.resolved} icon={Minus}
                     tone="text-sev-low" border="border-sev-low/40" />
                <Kpi label="Persistentes" value={data.counters.stable} icon={Circle}
                     tone="text-muted-foreground" border="border-border" />
              </div>

              {/* Delta score por severidad */}
              {data.severity_deltas && (
                <div className="border border-border p-3 mb-4" data-testid="compare-severity-deltas">
                  <div className="overline mb-2">Δ severidad · actual vs. anterior</div>
                  <div className="grid grid-cols-4 gap-3 text-xs font-mono">
                    {["critical", "high", "medium", "low"].map(sev => {
                      const cur = data.severity_deltas.current[sev] || 0;
                      const prev = data.severity_deltas.previous[sev] || 0;
                      const delta = cur - prev;
                      const t = SEV_TONE[sev];
                      const sign = delta > 0 ? "+" : "";
                      return (
                        <div key={sev} className="flex items-center justify-between">
                          <span className={cn("uppercase", t.tone)}>{t.label}</span>
                          <span>
                            <span className="text-foreground">{cur}</span>
                            <span className="text-muted-foreground">/{prev}</span>
                            <span className={cn("ml-1", delta > 0 ? "text-sev-critical" : delta < 0 ? "text-sev-low" : "text-muted-foreground")}>
                              ({sign}{delta})
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="flex gap-1 border-b border-border mb-3">
                {[
                  { k: "added", label: `NUEVOS · ${data.counters.added}` },
                  { k: "resolved", label: `RESUELTOS · ${data.counters.resolved}` },
                  { k: "stable", label: `PERSISTENTES · ${data.counters.stable}` },
                ].map(t => (
                  <button
                    key={t.k} onClick={() => setTab(t.k)}
                    data-testid={`compare-tab-${t.k}`}
                    className={cn("px-3 py-2 text-xs font-mono tracking-widest uppercase",
                                   tab === t.k
                                     ? "border-b-2 border-primary text-primary"
                                     : "text-muted-foreground hover:text-foreground")}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Findings list */}
              {rows.length === 0 ? (
                <div className="text-xs font-mono text-muted-foreground text-center py-8">
                  Ningún hallazgo en esta categoría.
                </div>
              ) : (
                <div className="space-y-2" data-testid={`compare-list-${tab}`}>
                  {rows.map((f, idx) => {
                    const t = SEV_TONE[f.severity] || SEV_TONE.low;
                    const Icon = t.icon;
                    const bar = tab === "added" ? "text-sev-critical" : tab === "resolved" ? "text-sev-low" : "text-muted-foreground";
                    const badge = tab === "added" ? "NUEVO" : tab === "resolved" ? "RESUELTO" : "PERSISTE";
                    return (
                      <div key={idx} className={cn("border p-3 flex items-start gap-3", t.border, t.bg)}
                           data-testid={`compare-finding-${idx}`}>
                        <Icon className={cn("w-4 h-4 shrink-0 mt-0.5", t.tone)} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={cn("px-1.5 py-0.5 text-[9px] font-mono tracking-widest uppercase font-bold border",
                                                 bar, "border-current")}>
                              {badge}
                            </span>
                            <span className={cn("text-[10px] font-mono tracking-widest uppercase", t.tone)}>
                              {t.label}
                            </span>
                            {f.cve && (
                              <span className="text-[10px] font-mono text-primary">{f.cve}</span>
                            )}
                          </div>
                          <div className="font-medium text-sm">{f.title}</div>
                          <div className="text-[11px] font-mono text-muted-foreground mt-1">
                            Activo: <b className="text-foreground">{f.asset_label || "—"}</b>
                          </div>
                          {f.description && (
                            <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                              {f.description}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone, border }) {
  return (
    <div className={cn("border p-4", border)} data-testid={`compare-kpi-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={cn("w-3 h-3", tone)} />
        <span className="overline text-[10px]">{label}</span>
      </div>
      <div className={cn("font-display text-3xl font-medium", tone)}>{value}</div>
    </div>
  );
}
